// Program time (constitution §4.9). This is the only file that names the time zone. Instants are UTC
// Date objects; calendar dates (due dates, "as of" days) are 'YYYY-MM-DD' strings in program time.

const PROGRAM_TIME_ZONE = 'America/Los_Angeles';

// Calendar date: matches "2027-03-14"; rejects "2027-3-14", "2027-03-14T00:00Z".
// https://regex101.com/?regex=%5E%5Cd%7B4%7D-%5Cd%7B2%7D-%5Cd%7B2%7D%24&flags=&testString=2027-03-14%0A2027-3-14%0A2027-03-14T00%3A00Z
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const DAY_MS = 86_400_000;
const SECOND_MS = 1000;
const YEAR_DIGITS = 4;
const FIELD_DIGITS = 2;
const ISO_DATE_LENGTH = 'YYYY-MM-DD'.length;

const partsFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: PROGRAM_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

const displayFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: PROGRAM_TIME_ZONE,
  weekday: 'short',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

interface WallClock {
  readonly year: number;
  readonly month: number;
  readonly day: number;
  readonly hour: number;
  readonly minute: number;
  readonly second: number;
}

function wallClock(instant: Date): WallClock {
  const parts: Readonly<Record<string, string>> = Object.fromEntries(
    partsFormatter.formatToParts(instant).map((p) => [p.type, p.value]),
  );
  return {
    year: Number(parts['year']),
    month: Number(parts['month']),
    day: Number(parts['day']),
    hour: Number(parts['hour']),
    minute: Number(parts['minute']),
    second: Number(parts['second']),
  };
}

const pad = (n: number, width = FIELD_DIGITS): string => String(n).padStart(width, '0');

export function isIsoDate(value: string): boolean {
  if (!ISO_DATE.test(value)) return false;
  const d = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().startsWith(value);
}

function assertIsoDate(value: string): void {
  if (!isIsoDate(value)) throw new RangeError(`not a calendar date (YYYY-MM-DD): ${value}`);
}

// The calendar date in program time at the given instant.
export function toProgramDate(instant: Date): string {
  const w = wallClock(instant);
  return `${pad(w.year, YEAR_DIGITS)}-${pad(w.month)}-${pad(w.day)}`;
}

export function today(now: Date = new Date()): string {
  return toProgramDate(now);
}

// Overdue flips at 00:00 program time on the day after the due date, for everyone (spec §8.10).
export function isOverdue(dueDate: string, now: Date): boolean {
  assertIsoDate(dueDate);
  return toProgramDate(now) > dueDate;
}

export function addDays(date: string, days: number): string {
  assertIsoDate(date);
  return new Date(Date.parse(`${date}T00:00:00Z`) + days * DAY_MS)
    .toISOString()
    .slice(0, ISO_DATE_LENGTH);
}

// Milliseconds the program zone is ahead of UTC at the instant (negative in Los Angeles).
function offsetMs(instant: number): number {
  const w = wallClock(new Date(instant));
  const asUtc = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);
  return asUtc - Math.floor(instant / SECOND_MS) * SECOND_MS;
}

// The UTC instant of 00:00 program time on the date. Two passes settle the offset on DST days.
export function programDayStart(date: string): Date {
  assertIsoDate(date);
  const midnightUtc = Date.parse(`${date}T00:00:00Z`);
  const first = midnightUtc - offsetMs(midnightUtc);
  return new Date(midnightUtc - offsetMs(first));
}

// Exclusive end of the program day: "as of <date>" means every event with at < programDayEnd(date).
export function programDayEnd(date: string): Date {
  return programDayStart(addDays(date, 1));
}

// Display form for people, e.g. "Wed, 04 Nov 2026, 09:30" (or the date part only).
export function formatLocal(
  instant: Date | string,
  style: 'datetime' | 'date' = 'datetime',
): string {
  const d = typeof instant === 'string' ? new Date(instant) : instant;
  if (Number.isNaN(d.getTime())) throw new RangeError('not a valid instant');
  const text = displayFormatter.format(d);
  return style === 'date' ? text.slice(0, text.lastIndexOf(',')) : text;
}
